import { runningAppVersion } from "./field_app_version"
import { logWarn } from "./field_logger"

// Card #4859 pt 1 -- the ONE shared "what version am I running vs what's
// actually deployed" state (Jay's design rule: no separate pending-update
// flags). Read by the Today/Places banner (field_update_banner_controller.js)
// and the Sync page's status card (field_update_status_controller.js), so the
// two can never show conflicting answers, and so Part 2 (admin-forced update)
// has one place to read from too.
//
// Scope A (field_service_worker.js's activate handler) deliberately stops a
// freshly-activated worker claiming an already-open tab on a real update, so
// that tab keeps running the OLD worker -- and the OLD page JS -- until the
// tasker chooses to move on. The new worker still tells every open tab its
// own APP_VERSION the moment it activates (postMessage reaches a tab
// regardless of which worker currently controls it); application.js's
// existing SW_UPDATED handler calls recordDeployedVersion() below only when
// that message says isUpdate: true -- never for a tab's own first install,
// where the "deployed" version and the one already running are the same
// build anyway.

const DEPLOYED_VERSION_KEY = "field:deployed_app_version"

// Stamps the latest version the SW has told this tab is actually deployed,
// and lets any mounted banner/status card know to re-check immediately
// rather than waiting for their own next connect.
export function recordDeployedVersion(version) {
  if (!version) return
  try {
    localStorage.setItem(DEPLOYED_VERSION_KEY, version)
  } catch {
    return // storage unavailable -- nothing to compare against
  }
  window.dispatchEvent(new CustomEvent("field:update-available"))
}

export function deployedAppVersion() {
  try {
    return localStorage.getItem(DEPLOYED_VERSION_KEY)
  } catch {
    return null
  }
}

// True once a real deploy has happened that this tab's own page -- its
// <meta name="app-version">, fixed at the moment this page was rendered --
// does not match. Self-clearing: the moment the tasker reloads (Update now,
// or any fresh navigation), the new page's meta tag matches the deployed
// version again and this goes back to false with no explicit reset needed.
export function updateAvailable() {
  const deployed = deployedAppVersion()
  const running  = runningAppVersion()
  return !!deployed && !!running && deployed !== running
}

// "Update now" -- Scope A already leaves the new worker fully active, just
// not in control of this tab. A plain reload is enough to pick it up: the
// browser assigns the already-active worker to the fresh navigation with no
// explicit clients.claim() call needed (claim() only ever mattered for a page
// that was already open, which this tab no longer is the moment reload()
// runs). No gating, no safe-point detection (rejected by Jay and Vanessa) --
// the tasker decides when to tap it.
export function activateUpdate() {
  window.location.reload()
}

// Card #4859 pt 2b -- the admin-forced half of this card. No tap, no banner,
// no gating (same "Jay and Vanessa rejected safe-point guessing" rule as
// activateUpdate() above): called every time this tasker's own 10 second
// ping reports a forced update pending (network_status_controller.js's
// "field:force-update-ping" event, sourced from Field::ForceUpdate.pending?
// on /field/ping). updateAvailable() is reused as the install-complete
// check rather than inventing a second one: it only goes true once
// field_service_worker.js's activate handler has actually fired for a real
// version change (card #4859 pt 1, Scope A), which itself only ever runs
// after the 'install' handler's own installComplete gate passed. A pending
// flag with no matching updateAvailable() yet means THIS device hasn't
// caught up regardless of what the server believed when the admin pulled
// the trigger -- stay on the old worker and just note why, once, so
// Signal's diagnostic log explains the gap instead of leaving it looking
// stuck. The note resets itself the moment the flag clears (pending false).
let _loggedForcedUpdateNotReady = false

export function applyForcedUpdate(pending) {
  if (!pending) {
    _loggedForcedUpdateNotReady = false
    return
  }

  if (updateAvailable()) {
    activateUpdate()
    return
  }

  if (_loggedForcedUpdateNotReady) return
  _loggedForcedUpdateNotReady = true
  logWarn("sw", "Forced update pending, not ready yet", {
    reason: "install not complete on this device"
  }).catch(() => {})
}
